<template>
  <div class="gomoku-container">
    <div class="board" @click="handleBoardClick" :style="boardStyles">
      <div
          v-for="(piece, index) in pieceList"
          :key="index"
          class="stone"
          :class="{
            'black': piece.player === 1,
            'white': piece.player === 2,
            'last-move': lastMove && lastMove.r === piece.r && lastMove.c === piece.c
          }"
          :style="getPieceStyle(piece.r, piece.c)"
      ></div>
    </div>

    <GameInfo
        :currentPlayer="currentPlayer"
        :winner="winner"
        :gameMode.sync="gameMode"
        :isThinking="isThinking"
        @reset="resetGame"
    />
  </div>
</template>

<script>
import GameInfo from './GameInfo.vue';

export default {
  name: 'GomokuBoard',
  components: { GameInfo },
  data() {
    return {
      boardState: [],
      currentPlayer: 1,
      winner: null,
      isGameOver: false,
      gameMode: 'PVA', // 'PVA' (Player vs AI) or 'PVP' (Player vs Player)
      aiPlayer: 2,
      isThinking: false,
      BOARD_SIZE: 15,
      CELL_SIZE: 40,
      aiScoreCache: {},
      humanScoreCache: {},
      lastMove: null,

      // --- 移除了所有联机功能相关 data ---
    };
  },
  computed: {
    boardStyles() {
      return {
        width: `${this.CELL_SIZE * (this.BOARD_SIZE - 1) + this.CELL_SIZE}px`,
        height: `${this.CELL_SIZE * (this.BOARD_SIZE - 1) + this.CELL_SIZE}px`,
        padding: `${this.CELL_SIZE / 2}px`,
        backgroundSize: `${this.CELL_SIZE}px ${this.CELL_SIZE}px`,
        backgroundImage: `
          repeating-linear-gradient(to right, transparent 0, transparent ${this.CELL_SIZE - 1}px, #333 ${this.CELL_SIZE - 1}px, #333 ${this.CELL_SIZE}px),
          repeating-linear-gradient(to bottom, transparent 0, transparent ${this.CELL_SIZE - 1}px, #333 ${this.CELL_SIZE - 1}px, #333 ${this.CELL_SIZE}px)
        `,
      };
    },
    pieceList() {
      const list = [];
      for (let r = 0; r < this.BOARD_SIZE; r++) {
        for (let c = 0; c < this.BOARD_SIZE; c++) {
          if (this.boardState[r][c] > 0) {
            list.push({ r, c, player: this.boardState[r][c] });
          }
        }
      }
      return list;
    }
  },

  created() {
    this.resetBoard();
    // --- 移除了 WebSocket 和服务器连接测试 ---
  },

  watch: {
    // 切换游戏模式时重置游戏
    gameMode() {
      this.resetGame();
    }
  },
  // --- 移除了 beforeDestroy 钩子 ---

  methods: {
    // --- 移除了所有 WebSocket 和联机相关的方法 ---
    // (testServerConnection, showNotification, initWebSocket, sendPing,
    // handleWebSocketMessage, createRoom, joinRoom, leaveRoom, sendChat, sendMoveToServer)

    getPieceStyle(r, c) {
      return {
        top: `${r * this.CELL_SIZE + this.CELL_SIZE / 2}px`,
        left: `${c * this.CELL_SIZE + this.CELL_SIZE / 2}px`,
      };
    },

    resetBoard() {
      this.boardState = Array(this.BOARD_SIZE).fill(0).map(() => Array(this.BOARD_SIZE).fill(0));
      this.aiScoreCache = {};
      this.humanScoreCache = {};
    },

    resetGame() {
      this.resetBoard();
      this.currentPlayer = 1;
      this.winner = null;
      this.isGameOver = false;
      this.isThinking = false;
      this.lastMove = null;

      // --- 移除了联机模式下的重置通知 ---
    },

    handleBoardClick(event) {
      if (this.isGameOver || this.isThinking) return;

      const boardRect = event.currentTarget.getBoundingClientRect();

      // --- 修复移动端缩放导致的点击偏移 ---
      // 1. 获取棋盘的逻辑（未缩放）宽度
      const logicalWidth = this.CELL_SIZE * (this.BOARD_SIZE - 1) + this.CELL_SIZE;
      // const logicalHeight = logicalWidth; // 棋盘是方形的
      const logicalPadding = this.CELL_SIZE / 2;

      // 2. 计算实际缩放比例
      //    getBoundingClientRect() 获取的是缩放后的尺寸
      const scale = boardRect.width / logicalWidth;

      // 3. 获取相对于元素边框的点击位置 (缩放后的坐标)
      const x = event.clientX - boardRect.left;
      const y = event.clientY - boardRect.top;

      // 4. 将点击坐标转换回逻辑（未缩放）坐标
      const logicalX = x / scale;
      const logicalY = y / scale;

      // 5. 使用逻辑坐标计算行列
      const c = Math.round((logicalX - logicalPadding) / this.CELL_SIZE);
      const r = Math.round((logicalY - logicalPadding) / this.CELL_SIZE);
      // --- 修复结束 ---

      if (r < 0 || r >= this.BOARD_SIZE || c < 0 || c >= this.BOARD_SIZE) {
        return;
      }

      this.handleCellClick(r, c);
    },

    handleCellClick(row, col) {
      if (this.isGameOver || this.boardState[row][col] !== 0 || this.isThinking) {
        return;
      }

      // --- 移除了联机模式的玩家权限检查 ---

      // PVA 模式下，阻止玩家在 AI 回合点击
      if (this.gameMode === 'PVA' && this.currentPlayer === this.aiPlayer) {
        return;
      }

      this.makeMove(row, col, this.currentPlayer);
    },

    makeMove(row, col, player) {
      this.$set(this.boardState[row], col, player);
      this.lastMove = { r: row, c: col };

      this.updateScoreCache(row, col);

      if (this.checkWin(row, col, player)) {
        this.isGameOver = true;
        this.winner = player;
        return;
      }

      if (this.isBoardFull()) {
        this.isGameOver = true;
        this.winner = 'draw';
        return;
      }

      this.currentPlayer = player === 1 ? 2 : 1;

      // 仅在 PVA 模式且轮到 AI 时触发 AI
      if (this.gameMode === 'PVA' && this.currentPlayer === this.aiPlayer) {
        this.$nextTick(() => this.aiMove(row, col));
      }
    },

    // --- AI 相关方法保持不变 ---
    isBoardFull() {
      for (let r = 0; r < this.BOARD_SIZE; r++) {
        for (let c = 0; c < this.BOARD_SIZE; c++) {
          if (this.boardState[r][c] === 0) {
            return false;
          }
        }
      }
      return true;
    },

    checkWin(row, col, player) {
      const directions = [
        [0, 1], [1, 0], [1, 1], [1, -1]
      ];

      for (const [dx, dy] of directions) {
        let count = 1;

        // 正向检查
        for (let i = 1; i < 5; i++) {
          const r = row + i * dx, c = col + i * dy;
          if (r >= 0 && r < this.BOARD_SIZE && c >= 0 && c < this.BOARD_SIZE &&
              this.boardState[r][c] === player) {
            count++;
          } else {
            break;
          }
        }

        // 反向检查
        for (let i = 1; i < 5; i++) {
          const r = row - i * dx, c = col - i * dy;
          if (r >= 0 && r < this.BOARD_SIZE && c >= 0 && c < this.BOARD_SIZE &&
              this.boardState[r][c] === player) {
            count++;
          } else {
            break;
          }
        }

        if (count >= 5) return true;
      }
      return false;
    },

    async aiMove(humanLastRow, humanLastCol) {
      this.isThinking = true;

      const thinkTime = 300 + Math.random() * 700;
      await new Promise(resolve => setTimeout(resolve, thinkTime));

      let bestScore = -Infinity;
      let bestMoves = [];

      const moves = this.getRelevantMoves(humanLastRow, humanLastCol);

      if (moves.length === 0) {
        this.makeMove(7, 7, this.aiPlayer);
        this.isThinking = false;
        return;
      }

      for (const {r, c} of moves) {
        const scoreAI = this.evaluateMove(r, c, this.aiPlayer);
        const scoreHuman = this.evaluateMove(r, c, 1);

        const totalScore = scoreAI * 0.9 + scoreHuman * 1.1;

        if (totalScore > bestScore) {
          bestScore = totalScore;
          bestMoves = [{r, c}];
        } else if (totalScore === bestScore) {
          bestMoves.push({r, c});
        }
      }

      const bestMove = bestMoves[Math.floor(Math.random() * bestMoves.length)];
      this.makeMove(bestMove.r, bestMove.c, this.aiPlayer);

      this.isThinking = false;
    },

    getRelevantMoves() {
      const relevantMoves = new Set();
      const searchRadius = 2;

      for (let r = 0; r < this.BOARD_SIZE; r++) {
        for (let c = 0; c < this.BOARD_SIZE; c++) {
          if (this.boardState[r][c] !== 0) {
            for (let dr = -searchRadius; dr <= searchRadius; dr++) {
              for (let dc = -searchRadius; dc <= searchRadius; dc++) {
                if (dr === 0 && dc === 0) continue;

                const nr = r + dr;
                const nc = c + dc;

                if (nr >= 0 && nr < this.BOARD_SIZE && nc >= 0 && nc < this.BOARD_SIZE &&
                    this.boardState[nr][nc] === 0) {
                  relevantMoves.add(`${nr},${nc}`);
                }
              }
            }
          }
        }
      }

      if (relevantMoves.size === 0 && this.boardState[7][7] === 0) {
        relevantMoves.add('7,7');
      }

      return Array.from(relevantMoves).map(s => {
        const [r, c] = s.split(',').map(Number);
        return {r, c};
      });
    },

    evaluateMove(r, c, player) {
      const cache = (player === this.aiPlayer) ? this.aiScoreCache : this.humanScoreCache;
      const cacheKey = `${r},${c},${player}`;

      if (cache[cacheKey] !== undefined) {
        return cache[cacheKey];
      }

      const board = this.boardState;
      let totalScore = 0;
      const directions = [[1, 0], [0, 1], [1, 1], [1, -1]];

      board[r][c] = player;

      for (const [dr, dc] of directions) {
        const lineScore = this.evaluateLine(r, c, dr, dc, player);
        totalScore += lineScore;
      }

      // 中心位置优势
      const centerDist = Math.abs(r - 7) + Math.abs(c - 7);
      totalScore += (14 - centerDist) * 2;

      board[r][c] = 0;

      cache[cacheKey] = totalScore;
      return totalScore;
    },

    evaluateLine(row, col, dr, dc, player) {
      const board = this.boardState;
      let score = 0;

      let count = 1;
      let openEnds = 0;

      // 正向检查
      let blocked = false;
      for (let i = 1; i <= 4; i++) {
        const r = row + i * dr, c = col + i * dc;
        if (r < 0 || r >= this.BOARD_SIZE || c < 0 || c >= this.BOARD_SIZE) {
          blocked = true;
          break;
        }
        if (board[r][c] === player) {
          count++;
        } else if (board[r][c] === 0) {
          openEnds++;
          break;
        } else {
          blocked = true;
          break;
        }
      }

      if (!blocked) {
        // 反向检查
        for (let i = 1; i <= 4; i++) {
          const r = row - i * dr, c = col - i * dc;
          if (r < 0 || r >= this.BOARD_SIZE || c < 0 || c >= this.BOARD_SIZE) {
            break;
          }
          if (board[r][c] === player) {
            count++;
          } else if (board[r][c] === 0) {
            openEnds++;
            break;
          } else {
            break;
          }
        }
      }

      // 评分逻辑
      if (count >= 5) {
        score += 100000;
      } else if (count === 4) {
        if (openEnds === 2) score += 10000;
        else if (openEnds === 1) score += 1000;
      } else if (count === 3) {
        if (openEnds === 2) score += 500;
        else if (openEnds === 1) score += 100;
      } else if (count === 2) {
        if (openEnds === 2) score += 50;
        else if (openEnds === 1) score += 10;
      } else if (count === 1 && openEnds === 2) {
        score += 5;
      }

      return score;
    },

    updateScoreCache(r, c) {
      const clearRadius = 3;
      for (let dr = -clearRadius; dr <= clearRadius; dr++) {
        for (let dc = -clearRadius; dc <= clearRadius; dc++) {
          const nr = r + dr, nc = c + dc;
          if (nr >= 0 && nr < this.BOARD_SIZE && nc >= 0 && nc < this.BOARD_SIZE) {
            delete this.aiScoreCache[`${nr},${nc},${this.aiPlayer}`];
            delete this.aiScoreCache[`${nr},${nc},1`];
            delete this.humanScoreCache[`${nr},${nc},${this.aiPlayer}`];
            delete this.humanScoreCache[`${nr},${nc},1`];
          }
        }
      }
    }
  }
};
</script>

<style scoped>
.gomoku-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  flex: 1;
}

.board {
  position: relative;
  background-color: #e5bf84;
  border: 2px solid #333;
  box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
  cursor: pointer;
  margin-bottom: 20px;

  /* 关键：设置 transform-origin，确保缩放时位置正确 */
  transform-origin: top center;
}

.stone {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  position: absolute;
  box-sizing: border-box;
  transform: translate(-50%, -50%);
  transition: all 0.1s ease;
  z-index: 10;
}

.stone.last-move::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff4136;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 5px #ff4136;
  z-index: 15;
}

.black {
  background-color: black;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.6);
}

.white {
  background-color: white;
  border: 1px solid #333;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
}

/* --- 响应式设计 (更新) --- */
/* 增加了更精细的断点，以适应小屏幕 */

@media (max-width: 768px) {
  .gomoku-container {
    padding: 10px;
  }
  .board {
    transform: scale(0.9); /* 600px * 0.9 = 540px */
  }
}

@media (max-width: 550px) {
  .board {
    transform: scale(0.8); /* 600px * 0.8 = 480px */
  }
}

@media (max-width: 480px) {
  .board {
    transform: scale(0.7); /* 600px * 0.7 = 420px */
  }
}

@media (max-width: 420px) {
  .board {
    transform: scale(0.6); /* 600px * 0.6 = 360px (适合 iPhone SE) */
  }
}

@media (max-width: 360px) {
  .board {
    transform: scale(0.5); /* 600px * 0.5 = 300px (适合非常窄的屏幕) */
  }
}

</style>
